import { type MessageKey, t } from '../../i18n';
import type { Me } from '../../lib/auth';
import { can } from '../../lib/permissions';

// Datos de /api/monitoreo (guía 7.12): sitios, servidor, incidentes y mantenimientos.

export type MonitorState = 'desconocido' | 'arriba' | 'lento' | 'caido';
export type MonitorStatus = 'activo' | 'pausado' | 'por_revisar';
export type MonitorSource = 'manual' | 'cloudpanel' | 'directorio';
export type IncidentKind = 'caida' | 'certificado' | 'cpu' | 'memoria' | 'swap' | 'disco' | 'respaldo' | 'cola_correo';
export type Period = '24h' | '7d' | '30d';

export interface Uptime {
  day: number | null;
  week: number | null;
  month: number | null;
  latencyMs: number | null;
}

export interface TargetSummary {
  id: string;
  name: string;
  url: string;
  status: MonitorStatus;
  source: MonitorSource;
  state: MonitorState;
  stateSince: string | null;
  lastCheckedAt: string | null;
  lastLatencyMs: number | null;
  lastStatusCode: number | null;
  lastError: string | null;
  certExpiresAt: string | null;
  certDays: number | null;
  certIssuer: string | null;
  missingSince: string | null;
}

export interface TargetRow extends TargetSummary {
  app: { id: string; name: string } | null;
  uptime: Uptime;
  down: boolean;
}

export interface Incident {
  id: string;
  kind: IncidentKind;
  title: string;
  detail: string | null;
  targetId: string | null;
  hostId: string | null;
  subject: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  notifications: number;
}

export interface Maintenance {
  id: string;
  targetId: string | null;
  target: string | null;
  startsAt: string;
  endsAt: string;
  reason: string;
  createdBy: string | null;
  state: 'programado' | 'en_curso' | 'terminado';
}

export interface HostSummary {
  id: string;
  name: string;
  os: string | null;
  cpuCount: number | null;
  memoryTotalBytes: number | null;
  bootAt: string | null;
  lastSampleAt: string | null;
  stale: boolean;
}

export interface SampleDetail {
  disks?: Array<{ path: string; usedPercent: number; freeBytes: number; totalBytes: number }>;
  postgres?: { connections: number; sizeBytes: number } | null;
  mail?: { waiting: number; active: number; delayed: number; failed: number; oldestWaitingMinutes: number | null } | null;
  backupHours?: number | null;
}

export interface Sample {
  sampledAt: string;
  cpu: number;
  load: number | null;
  memory: number;
  swap: number | null;
  disk: number | null;
  diskFreeBytes: number | null;
  detail: SampleDetail;
}

export interface Can {
  manage: boolean;
  acknowledge: boolean;
}

export interface Summary {
  counts: { arriba: number; lento: number; caido: number; desconocido: number; porRevisar: number; pausados: number };
  targets: TargetRow[];
  server: { host: HostSummary; latest: Sample | null; trend: Array<{ at: string; cpu: number; memory: number }> } | null;
  incidents: Incident[];
  maintenance: Maintenance[];
  can: Can;
}

export interface TargetDetail extends TargetSummary {
  expectedStatusMin: number;
  expectedStatusMax: number;
  keyword: string | null;
  sourceKey: string | null;
  app: { id: string; name: string } | null;
  ownerId: string | null;
  owner: string | null;
  appOwner: string | null;
  uptime: Uptime;
  series: Array<{ at: string; uptime: number | null; latencyMs: number | null; maxLatencyMs: number }>;
  days: Array<{ day: string; uptime: number | null }>;
  incidents: Incident[];
  maintenance: Maintenance[];
  can: Can;
}

export interface ServerData {
  hosts: Array<{ id: string; name: string }>;
  host: HostSummary | null;
  latest: Sample | null;
  series: Array<{ at: string; cpu: number; cpuMax: number; memory: number; swap: number | null; disk: number | null; load: number | null }>;
  incidents: Incident[];
}

export interface MonitoringForm {
  apps: Array<{ id: string; name: string; url: string | null; ownerId: string | null }>;
  owners: Array<{ id: string; fullName: string }>;
  can: Can;
}

export const canSeeMonitoring = (me: Me) => ['monitoreo.ver', 'monitoreo.gestionar', 'monitoreo.avisos'].some((p) => can(me, p));

export const stateName = (s: MonitorState) => t(`mon.estado_${s}` as MessageKey);
export const kindName = (k: IncidentKind) => t(`mon.tipo_${k}` as MessageKey);
export const sourceName = (s: MonitorSource) => t(`mon.origen_${s}` as MessageKey);

/** Estado que se muestra: el de la revisión si se vigila; si no, el de la configuración (pausado, por revisar). */
export function displayState(s: Pick<TargetSummary, 'status' | 'state'>): { label: string; tone: 'slate' | 'blue' | 'amber' | 'red' } {
  if (s.status !== 'activo') return { label: t(`mon.estado_${s.status}` as MessageKey), tone: s.status === 'por_revisar' ? 'amber' : 'slate' };
  const tone = s.state === 'caido' ? 'red' : s.state === 'lento' ? 'amber' : s.state === 'arriba' ? 'blue' : 'slate';
  return { label: stateName(s.state), tone };
}

export const percent = (n: number | null | undefined, digits = 1) =>
  n === null || n === undefined ? t('mon.sin_datos') : `${n.toLocaleString('es-PA', { maximumFractionDigits: digits })} %`;

/** Tamaño en GB (o en MB si no llega a 1 GB, para que una base chica no diga "0 GB"). */
export const gb = (bytes: number | null | undefined) => {
  if (bytes === null || bytes === undefined) return t('mon.sin_datos');
  if (bytes < 1024 ** 3) return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
  return `${(bytes / 1024 ** 3).toLocaleString('es-PA', { maximumFractionDigits: 1 })} GB`;
};

export function formatDuration(ms: number): string {
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? 'día' : 'días'}${hours % 24 ? ` ${hours % 24} h` : ''}`;
}

export const formatDateTime = (iso: string) => new Date(iso).toLocaleString('es-PA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
export const formatDate = (iso: string) => new Date(iso).toLocaleDateString('es-PA', { day: 'numeric', month: 'long', year: 'numeric' });

/** Hace cuánto, en palabras cortas ("hace 3 min"). */
export function ago(iso: string, now = Date.now()): string {
  return `hace ${formatDuration(now - new Date(iso).getTime())}`;
}

/** Disponibilidad por día: bien, regular, mal o sin datos (el color siempre va con su leyenda y su texto). */
export function uptimeBand(v: number | null): 'bien' | 'regular' | 'mal' | 'sin' {
  if (v === null) return 'sin';
  if (v >= 99.9) return 'bien';
  if (v >= 99) return 'regular';
  return 'mal';
}

/** Valor para un <input type="datetime-local"> en la hora local del navegador. */
export function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
